import { useState } from "react";
import { useNavigate } from "react-router";
import { When } from "react-if";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {
  ISenderDetail,
  QuoteEventType,
  QuoteStatus,
  langauge,
  quoteEventLanguageMap,
  viewableQuoteStatuses,
  getAccountingSystemDeepLink,
} from "shared";
import {
  CloudArrowDownIcon,
  FaceFrownIcon,
  FaceSmileIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/20/solid";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";

import { useQuote } from "../../hooks/useQuote";
import { useActions, useActionTableModels } from "../../hooks/useActions";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useAccountingSystemTerm } from "../../hooks/useAccountingSystemTerm";
import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useSettings } from "../../hooks/useSettings";
import { iconMap } from "../../constants/iconMap";
import { Badge } from "../../../common/Atoms/Badge";
import { Card } from "../../../common/Atoms/Card";
import { Divider } from "../../../common/Atoms/Divider";
import { FixedDimensionsGrid } from "../../../common/Atoms/FixedDimensionsGrid";
import { Strap } from "../../../common/Atoms/Strap";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { SmallSpinner } from "../../../common/Atoms/SmallSpinner";
import { DatePopover } from "../../../common/Components/DatePopover";
import { InvoiceSenderDetail } from "../../../common/Components/Invoice/InvoiceSenderDetail";
import { classNames } from "../../../common/lib/classNames";
import { env } from "../../../common/lib/env";
import { IHeroIcon } from "../../types/types";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";
import { Timeline } from "../TimeLine/Timeline";
import { WrappedLink } from "../WrappedLink";

const statusColors: { [key in QuoteStatus]: `gray` | `blue` | `green` | `red` | `purple` } = {
  [QuoteStatus.DRAFT]: `gray`,
  [QuoteStatus.SENT]: `blue`,
  [QuoteStatus.ACCEPTED]: `green`,
  [QuoteStatus.DECLINED]: `red`,
  [QuoteStatus.CONVERTED]: `purple`,
  [QuoteStatus.DELETED]: `red`,
  [QuoteStatus.UNKNOWN]: `gray`,
};

const eventIconMap: {
  [key in QuoteEventType]: {
    icon: IHeroIcon;
    className: string;
  }
} = {
  [QuoteEventType.IMPORTED]: {
    icon: CloudArrowDownIcon,
    className: `bg-paidniceGreen-300`,
  },
  [QuoteEventType.SENT]: {
    icon: PaperAirplaneIcon,
    className: `bg-blue-500`,
  },
  [QuoteEventType.ACCEPTED]: {
    icon: FaceSmileIcon,
    className: `bg-green-500`,
  },
  [QuoteEventType.DECLINED]: {
    icon: FaceFrownIcon,
    className: `bg-red-500`,
  },
};

function QuoteActionOverview({ quoteNumber }: { quoteNumber: string }) {
  const { data: actionsData, isLoading, isFetching } = useActions();

  const actions = actionsData?.actions;

  return (
    <div className={ `lg:col-start-3 bg-white p-6 ring-1 ring-gray-900/5 rounded-lg` }>
      { /* Activity feed */ }
      <div>
        <h2 className={ `text-sm font-semibold leading-6 text-gray-900 flex items-center` }>{ `Action Overview` }</h2>
        <p className={ `text-sm text-gray-500` }>
          { `Calculated automatically several times daily.` }
        </p>
      </div>
      <LoadingOverlay
        loading={ isLoading || isFetching }
        contrast={ `light` }
      >
        <Divider />
        <Timeline
          actions={ actions }
          viewMoreSearchStr={ quoteNumber }
          context={ `quote` }
          emptyMessage={
            (
              <span>
                { `No upcoming events.` }
                <br/>
                <br/>
                { `Quote based policies will schedule their actions here` }
              </span>
            )
          }/>
      </LoadingOverlay>
    </div>
  );
}

function QuoteEvents() {
  const { quote, isLoading } = useQuote();

  const timeline = quote?.events?.map(e => ({
    id: e.id,
    content: quoteEventLanguageMap.timeline[e.type].description,
    datetime: new Date(e.createdAt).toLocaleString(),
    icon: eventIconMap[e.type].icon,
    iconBackground: eventIconMap[e.type].className,
  })) || [];

  return (
    <Card className={ `mt-6 flow-root` }>
      <LoadingOverlay loading={ isLoading }>
        <ul role={ `list` }
          className={ `-mb-8` }>
          { timeline.map((event, eventIdx) => (
            <li key={ event.id }>
              <div className={ `relative pb-8` }>
                { eventIdx !== timeline.length - 1 ? (
                  <span className={ `absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200` }
                    aria-hidden={ `true` } />
                ) : null }
                <div className={ `relative flex space-x-3` }>
                  <div>
                    <span
                      className={ classNames(
                        event.iconBackground,
                        `h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white`,
                      ) }
                    >
                      <event.icon className={ `h-5 w-5 text-white` }
                        aria-hidden={ `true` } />
                    </span>
                  </div>
                  <div className={ `flex min-w-0 flex-1 justify-between space-x-4 pt-1.5` }>
                    <div>
                      <p className={ `text-sm text-gray-500` }>
                        { event.content }{ ` ` }
                      </p>
                    </div>
                    <div className={ `whitespace-nowrap text-right text-sm text-gray-500` }>
                      <time dateTime={ event.datetime }>{ event.datetime }</time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          )) }
        </ul>

        <Paragraph
          className={ `mt-6` }
          variant={ `help` }
        >
          { `Only a few key quote lifecycle events are recorded here. Refer to actions for things like emails.` }
        </Paragraph>
      </LoadingOverlay>
    </Card>
  );
}

export function QuotePage() {
  const { quote, isLoading } = useQuote();
  const currentOrg = useGetSelectedOrganisation();
  const { data: settings } = useSettings();
  const quoteTerm = useAccountingSystemTerm(`Quote`);
  const accountingSystemName = useAccountingSystemName();
  const { setFilterModel, setCurrentTab } = useActionTableModels();
  const navigate = useNavigate();
  const request = useApiRequest();
  const [downloadingPO, setDownloadingPO] = useState(false);

  if (isLoading) {
    return (
      <div className={ `flex justify-center items-center h-96` }>
        <SmallSpinner
          size={ `lg` }
          colored
        />
      </div>
    );
  }

  if (!quote || !currentOrg) return null;

  const quoteExternalLink = getAccountingSystemDeepLink(currentOrg, `quote`, quote.externalId);

  const senderDetail: ISenderDetail = {
    customerFacingCompanyName: currentOrg.legalName,
    companyNumber: settings?.companyNumber,
    companyNumberType: settings?.companyNumberType,
    taxNumber: settings?.taxNumber,
  };

  const hasDiscounts = quote.linesContainDiscounts;
  const expired = quote.hasExpired && quote.status === QuoteStatus.SENT;
  const totalColSpan = hasDiscounts ? 4 : 3;
  const shouldRenderCurrency = !/[a-z]/i.test(quote.amountTotalFormatted ?? ``);

  function goToActions(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();
    // Make sure we on the all tab
    setCurrentTab(null);

    // Quick search covers quote numbers
    setFilterModel({
      items: [],
      quickFilterValues: [quote.number],
    });

    navigate(`/${currentOrg.id}/actions?persist=true`);
  }

  async function onDownloadPO(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault();

    if (downloadingPO) return;

    setDownloadingPO(true);

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}quotes/${quote.id}/download-po`,
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;
      link.setAttribute(`download`, quote.customerDataPOFilename || `quote-PO-${quote.number}` );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (err) {
      const responseObj = await err.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download P.O.`}`);
    }

    setDownloadingPO(false);
  }

  const quickActionClass = `group flex w-full items-center justify-between rounded-md border border-gray-200 bg-white px-2.5 py-1.5 transition-colors hover:border-gray-300 hover:bg-gray-50`;
  const quickActionLabelClass = `text-sm font-medium leading-5 text-gray-700`;
  const quickActionIconClass = `h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600`;
  const quickActionArrowClass = `ml-3 text-base leading-none text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-600`;
  const ViewQuoteActionsIcon = iconMap.viewInvoiceActions;
  const ViewInAccountingSystemIcon = iconMap.viewInAccountingSystem;
  const ViewInCustomerPortalIcon = iconMap.viewInCustomerPortal;

  return (
    <div>
      <div className={
        classNames(
          `mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3`,
        )
      }>
        { /* Quick actions */ }
        <div className={ `lg:col-start-3 lg:row-end-1` }>
          <Card>
            <div className={ `flex justify-between pt-2` }>
              <Paragraph>
                { `${quoteTerm} Value` }
              </Paragraph>
              <Paragraph
                variant={ `secondary` }
              >
                { quote.amountTotalFormatted }
              </Paragraph>
            </div>

            <When condition={ !!quote.expiryDateString }>
              <div className={ `flex justify-between pt-2` }>
                <Paragraph>
                  { expired ? `Expired` : `Expires` }
                </Paragraph>
                <Paragraph
                  variant={ `secondary` }
                  className={ classNames(expired && `text-red-600`) }
                >
                  <DatePopover
                    date={ quote.expiryDateString }
                    labelFormat={ `dd LLL yyyy` }
                    organisationTimezone={ currentOrg?.validatedTimezone }
                    time={ `end` }
                  />
                </Paragraph>
              </div>
            </When>

            <Divider
              weight={ `light` }
              text={ `Quick Actions` }
              spacing={ `small` }
            />

            <FixedDimensionsGrid className={ `mt-3 grid grid-cols-2 gap-1.5` }>
              <a
                href={ `#` }
                onClick={ goToActions }
                className={ quickActionClass }
              >
                <span
                  data-fixed-dimensions-content={ `true` }
                  className={ `flex min-w-0 items-center gap-2` }
                >
                  <ViewQuoteActionsIcon className={ quickActionIconClass } />
                  <span className={ quickActionLabelClass }>{ `View ${quoteTerm} Actions` }</span>
                </span>
                <span aria-hidden={ `true` }
                  className={ quickActionArrowClass }>&rarr;</span>
              </a>

              <When condition={ !!quoteExternalLink }>
                <a
                  href={ quoteExternalLink }
                  target={ `_blank` }
                  rel={ `noopener noreferrer` }
                  className={ quickActionClass }
                >
                  <span
                    data-fixed-dimensions-content={ `true` }
                    className={ `flex min-w-0 items-center gap-2` }
                  >
                    <ViewInAccountingSystemIcon className={ quickActionIconClass } />
                    <span className={ quickActionLabelClass }>{ `View in ` + accountingSystemName }</span>
                  </span>
                  <span aria-hidden={ `true` }
                    className={ quickActionArrowClass }>&rarr;</span>
                </a>
              </When>

              <When condition={ viewableQuoteStatuses.includes(quote.status) }>
                <a
                  href={ `${env.portalWebUrl}/q/${quote.hash}` }
                  target={ `_blank` }
                  rel={ `noopener noreferrer` }
                  className={ quickActionClass }
                >
                  <span
                    data-fixed-dimensions-content={ `true` }
                    className={ `flex min-w-0 items-center gap-2` }
                  >
                    <ViewInCustomerPortalIcon className={ quickActionIconClass } />
                    <span className={ quickActionLabelClass }>{ `View in Customer Portal` }</span>
                  </span>
                  <span aria-hidden={ `true` }
                    className={ quickActionArrowClass }>&rarr;</span>
                </a>
              </When>

              <When condition={ !!quote.customerDataPOUploadUrl }>
                <a
                  href={ `#` }
                  onClick={ onDownloadPO }
                  className={ quickActionClass }
                >
                  <span
                    data-fixed-dimensions-content={ `true` }
                    className={ `flex min-w-0 items-center gap-2` }
                  >
                    <DocumentArrowDownIcon className={ quickActionIconClass } />
                    <span className={ quickActionLabelClass }>
                      { downloadingPO ? `Downloading...` : `Download P.O.` }
                    </span>
                  </span>
                  <span aria-hidden={ `true` }
                    className={ quickActionArrowClass }>&rarr;</span>
                </a>
              </When>
            </FixedDimensionsGrid>
          </Card>
        </div>

        { /* Quote document */ }
        <div className={
          classNames(
            `bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg lg:col-span-2 lg:row-span-2 lg:row-end-2`,
          )
        }>
          <When condition={ expired }>
            <Strap
              type={ `warning` }
              className={ `sm:rounded-t-lg` }
            >
              { `This ${quoteTerm.toLowerCase()} has expired` }
            </Strap>
          </When>

          <When condition={ quote.status === QuoteStatus.DELETED }>
            <Strap
              type={ `error` }
              className={ classNames(
                expired ? `` : `sm:rounded-t-lg`,
              ) }
            >
              { `This ${quoteTerm.toLowerCase()} is deleted.` }
            </Strap>
          </When>

          <div className={ `-mx-4 px-4 py-8 sm:mx-0 sm:px-8 sm:pb-14 xl:px-16 xl:pb-20 xl:pt-16` }>
            <div className={ `flex justify-between` }>
              <Heading>
                { quote.number ? `${quoteTerm} ${quote.number}` : quoteTerm }
              </Heading>
              <Badge
                message={ langauge.quotes.status[quote.status] }
                color={ statusColors[quote.status] }
              />
            </div>

            <When condition={ quote.referenceText }>
              <Paragraph
                as={ `h3` }
              >
                { `${quote.referenceText}` }
              </Paragraph>
            </When>

            <dl className={ `mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2` }>
              <div className={ `sm:pr-4` }>
                <dt className={ `inline text-gray-500` }>{ `Issued on` }</dt>{ ` ` }
                <dd className={ `inline text-gray-700` }>
                  <time>
                    <DatePopover
                      date={ quote.issueDateString }
                      organisationTimezone={ currentOrg?.validatedTimezone }
                      labelFormatPreset={ DateTime.DATE_HUGE }
                    />
                  </time>
                </dd>
              </div>
              <When condition={ !!quote.expiryDateString }>
                <div className={ `mt-2 sm:mt-0 sm:pl-4` }>
                  <dt className={ `inline text-gray-500` }>{ expired ? `Expired on` : `Expires on` }</dt>{ ` ` }
                  <dd className={ classNames(
                    `inline`,
                    expired ? `text-red-600` : `text-gray-700`,
                  ) }>
                    <time>
                      <DatePopover
                        date={ quote.expiryDateString }
                        organisationTimezone={ currentOrg?.validatedTimezone }
                        time={ `end` }
                        labelFormatPreset={ DateTime.DATE_HUGE }
                      />
                    </time>
                  </dd>
                </div>
              </When>
              <div className={ `mt-6 border-t border-gray-900/5 pt-6 sm:pr-4` }>
                <InvoiceSenderDetail
                  senderDetail={ senderDetail }
                />
              </div>
              <div className={ `mt-8 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pl-4 sm:pt-6` }>
                <dt className={ `font-semibold text-gray-900` }>{ `To` }</dt>
                <dd className={ `mt-2 text-gray-500` }>
                  <div className={ `flex mb-1` }>
                    <Heading
                      variant={ `secondary` }
                    >
                      <WrappedLink
                        to={ `/contacts/${quote.contactId}` }
                        className={ `hover:underline text-indigo-600 hover:text-indigo-800 ` }
                      >
                        { quote.contactName }
                      </WrappedLink>
                    </Heading>
                  </div>
                </dd>
              </div>
            </dl>

            { /* Title & Summary (if any) */ }
            <When condition={ quote.titleText || quote.summaryText }>
              <div className={ `mt-6` }>
                <When condition={ quote.titleText }>
                  <Heading>
                    { quote.titleText }
                  </Heading>
                </When>

                <When condition={ quote.summaryText }>
                  <Paragraph
                    variant={ `secondary` }
                    className={ `whitespace-pre-wrap` }
                  >
                    { quote.summaryText }
                  </Paragraph>
                </When>
              </div>
            </When>

            <table className={ `mt-16 w-full whitespace-nowrap text-left text-sm leading-6` }>
              <colgroup>
                <col className={ `w-full` } />
                <col />
                <When condition={ hasDiscounts }>
                  <col />
                </When>
                <col />
                <col />
              </colgroup>
              <thead className={ `border-b border-gray-200 text-gray-900` }>
                <tr>
                  <th scope={ `col` }
                    className={ `px-0 py-3 font-semibold` }>
                    { `Description` }
                  </th>
                  <th scope={ `col` }
                    className={ `hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell` }>
                    { `Quantity` }
                  </th>
                  <When condition={ hasDiscounts }>
                    <th scope={ `col` }
                      className={ `hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell` }>
                      { `Discount` }
                    </th>
                  </When>
                  <th scope={ `col` }
                    className={ `hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell` }>
                    { `Amount` }
                  </th>
                  <th scope={ `col` }
                    className={ `py-3 pl-8 pr-0 text-right font-semibold` }>
                    { `Total` }
                  </th>
                </tr>
              </thead>
              <tbody>
                { quote.lineItems?.map((lineItem, index) => (
                  <tr
                    className={ `border-b border-gray-100` }
                    key={ `lineitem-${index}-${lineItem.description}` }
                  >
                    <td className={ `max-w-0 px-0 py-5 align-top` }>
                      <div className={ `truncate text-gray-500 whitespace-break-spaces` }>{ lineItem.description }</div>
                    </td>
                    <td className={ `hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell` }>
                      { lineItem.quantity }
                    </td>
                    <When condition={ hasDiscounts }>
                      <td className={ `hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell` }>
                        { lineItem.discountAmountFormatted }
                      </td>
                    </When>
                    <td className={ `hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell` }>
                      { lineItem.itemAmountFormatted }
                    </td>
                    <td className={ `py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700` }>
                      { lineItem.amountTotalFormatted }
                    </td>
                  </tr>
                )) }
              </tbody>
              <tfoot>
                <When condition={ quote.subTotalAmountFormatted }>
                  <tr>
                    <th scope={ `row` }
                      className={ `px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden` }>
                      { `Subtotal` }
                    </th>
                    <th
                      scope={ `row` }
                      colSpan={ totalColSpan }
                      className={ `hidden px-0 pb-0 pt-6 text-right font-normal text-gray-700 sm:table-cell` }
                    >
                      { `Subtotal` }
                    </th>
                    <td className={ `pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900` }>
                      { quote.subTotalAmountFormatted }
                    </td>
                  </tr>
                </When>

                <When condition={ quote.totalDiscountAmountFormatted }>
                  <tr>
                    <th scope={ `row` }
                      className={ `pt-4 font-normal text-gray-700 sm:hidden` }>
                      { `Discounts` }
                    </th>
                    <th
                      scope={ `row` }
                      colSpan={ totalColSpan }
                      className={ `hidden pt-4 text-right font-normal text-gray-700 sm:table-cell` }
                    >
                      { `Discounts` }
                    </th>
                    <td className={ `pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900` }>
                      { quote.totalDiscountAmountFormatted }
                    </td>
                  </tr>
                </When>

                <When condition={ quote.totalTaxAmountFormatted }>
                  <tr>
                    <th scope={ `row` }
                      className={ `pt-4 font-normal text-gray-700 sm:hidden` }>
                      { `Tax` }
                    </th>
                    <th
                      scope={ `row` }
                      colSpan={ totalColSpan }
                      className={ `hidden pt-4 text-right font-normal text-gray-700 sm:table-cell` }
                    >
                      { `Tax` }
                    </th>
                    <td className={ `pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900` }>
                      { quote.totalTaxAmountFormatted }
                    </td>
                  </tr>
                </When>

                <tr>
                  <th scope={ `row` }
                    className={ `pt-4 font-semibold text-gray-900 sm:hidden` }>
                    { `Total` }
                  </th>
                  <th
                    scope={ `row` }
                    colSpan={ totalColSpan }
                    className={ `hidden pt-4 text-right font-semibold text-gray-900 sm:table-cell` }
                  >
                    { `Total` }
                  </th>
                  <td className={ `pb-0 pl-8 pr-0 pt-4 text-right font-semibold tabular-nums text-gray-900` }>
                    { quote.amountTotalFormatted }
                    { shouldRenderCurrency && quote.currency }
                  </td>
                </tr>
              </tfoot>
            </table>

            { /* Terms */ }
            <When condition={ quote.termsText }>
              <Paragraph
                className={ `mt-8` }
              >
                { `Terms: ` }
                <br/>
                <Paragraph
                  as={ `span` }
                  variant={ `secondary` }
                  className={ `whitespace-pre-wrap` }
                >
                  { `${quote.termsText}` }
                </Paragraph>
              </Paragraph>
            </When>

            { /* Purchase order info */ }
            <When condition={ quote.customerDataPONumber }>
              <Paragraph
                className={ `mt-4` }
              >
                { `Purchase Order Number: ` }
                <Paragraph
                  as={ `span` }
                  variant={ `secondary` }
                >
                  { quote.customerDataPONumber }
                </Paragraph>
              </Paragraph>
            </When>

            <When condition={ quote.customerDataPOFilename }>
              <Paragraph>
                { `Uploaded Purchase Order: ` }
                <Paragraph
                  as={ `span` }
                  variant={ `secondary` }
                >
                  { quote.customerDataPOFilename }
                </Paragraph>
              </Paragraph>
            </When>
          </div>
        </div>

        <QuoteActionOverview
          quoteNumber={ quote.number }
        />
      </div>

      <QuoteEvents />
    </div>
  );
}
